/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import { Spin } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../ts-components/Text'
import CalculatorIcon from '../../icons/CalculatorIcon'

/**
 * @interface AudienceBreakdownData
 * @property {number} total_users - Total number of users in the audience
 * @property {number} following_users - Number of users following the entity
 * @property {number} player_users - Number of player users
 * @property {number} official_users - Number of official users
 * @property {number} team_official_users - Number of team official users
 * @property {number} league_admin_users - Number of league admin users
 * @property {number} videographer_users - Number of videographer users
 * @property {number} admin_users - Number of admin users
 */
export interface AudienceBreakdownData {
  total_users: number
  following_users: number
  player_users: number
  official_users: number
  team_official_users: number
  league_admin_users: number
  videographer_users: number
  admin_users: number
}

/**
 * @interface AudienceBreakdownSectionProps
 * @property {string | undefined} selectedEntityId - ID of the selected entity
 * @property {string | null} selectedEntityType - Type of the selected entity ('org', 'team', 'sched')
 * @property {AudienceBreakdownData | null} [audienceData] - Optional audience data to display
 * @property {boolean} [isLoading] - Optional loading state indicator
 * @property {() => void} [onCalculate] - Optional callback function to trigger calculation
 * @property {boolean} [isCalculating] - Optional calculating state indicator
 * @property {boolean} [hasCalculated] - Whether audience has been calculated for current entity
 */
interface AudienceBreakdownSectionProps {
  selectedEntityId: string | undefined
  selectedEntityType: string | null
  audienceData?: AudienceBreakdownData | null
  isLoading?: boolean
  onCalculate?: () => void
  isCalculating?: boolean
  hasCalculated?: boolean
}

/**
 * Audience Breakdown Section component - displays detailed audience statistics
 *
 * This component shows detailed audience breakdown statistics when an entity is selected.
 * It fetches audience data from the API and displays it in a clean format.
 *
 * Key features:
 * - Fetches and displays detailed audience breakdown
 * - Handles loading states and API errors gracefully
 * - Updates automatically when entity selection changes
 * - Only renders when an entity is selected
 *
 * @param props - Component props as defined in AudienceBreakdownSectionProps
 * @returns JSX element representing the audience breakdown section
 */
const AudienceBreakdownSection: React.FC<AudienceBreakdownSectionProps> =
  React.memo(
    ({
      selectedEntityId,
      selectedEntityType,
      audienceData = null,
      isLoading = false,
      onCalculate,
      isCalculating = false,
      hasCalculated = false,
    }) => {
      // Always render the container, but show "No audience data available" if no entity is selected
      const hasSelectedEntity = selectedEntityId && selectedEntityType

      return (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            height: 100%;
          `}
        >
          {/* Header row with title, subtitle and button */}
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 16px;
            `}
          >
            <div
              css={css`
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 4px;
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                Audience Breakdown
              </Text>
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_100}
              >
                {!hasSelectedEntity
                  ? 'Select an Entity to Calculate Audience'
                  : audienceData
                  ? 'Check Your Audience Breakdown'
                  : 'Click to Calculate Audience'}
              </Text>
            </div>

            {hasSelectedEntity && onCalculate && (
              <button
                type='button'
                onClick={onCalculate}
                disabled={isCalculating}
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background-color: transparent;
                  border: 1px solid ${colors.PRIMARY};
                  border-radius: 6px;
                  cursor: ${isCalculating ? 'not-allowed' : 'pointer'};
                  opacity: ${isCalculating ? 0.5 : 1};
                  padding: 8px 12px;
                  gap: 8px;
                `}
                aria-label='Calculate Audience'
              >
                {isCalculating ? (
                  <Spin size='small' />
                ) : (
                  <CalculatorIcon size={24} color={colors.PRIMARY} />
                )}
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={colors.PRIMARY}
                >
                  {hasCalculated ? 'Re-Calculate' : 'Calculate'}
                </Text>
              </button>
            )}
          </div>

          {/* Audience data section */}
          {hasSelectedEntity && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
              `}
            >
              {isLoading ? (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <Spin size='small' />
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_100}
                  >
                    Loading...
                  </Text>
                </div>
              ) : audienceData ? (
                <>
                  {audienceData.admin_users > 0 ? (
                    <Text
                      variant='paragraphXSmall'
                      weight='regular'
                      color={colors.NEUTRAL_50}
                    >
                      Admins:{' '}
                      {(audienceData?.admin_users || 0).toLocaleString()} User
                      {audienceData?.admin_users > 1 ? 's' : ''}
                    </Text>
                  ) : null}
                  {audienceData.league_admin_users > 0 ? (
                    <Text
                      variant='paragraphXSmall'
                      weight='regular'
                      color={colors.NEUTRAL_50}
                    >
                      League Admins:{' '}
                      {(audienceData?.league_admin_users || 0).toLocaleString()}{' '}
                      User
                      {audienceData?.league_admin_users > 1 ? 's' : ''}
                    </Text>
                  ) : null}
                  {audienceData.official_users > 0 ? (
                    <Text
                      variant='paragraphXSmall'
                      weight='regular'
                      color={colors.NEUTRAL_50}
                    >
                      Officials: {audienceData.official_users.toLocaleString()}{' '}
                      User
                      {audienceData.official_users > 1 ? 's' : ''}
                    </Text>
                  ) : null}
                  {audienceData.videographer_users > 0 ? (
                    <Text
                      variant='paragraphXSmall'
                      weight='regular'
                      color={colors.NEUTRAL_50}
                    >
                      Videographers:{' '}
                      {(audienceData?.videographer_users || 0).toLocaleString()}{' '}
                      User
                      {audienceData?.videographer_users > 1 ? 's' : ''}
                    </Text>
                  ) : null}
                  {audienceData.following_users > 0 ? (
                    <Text
                      variant='paragraphXSmall'
                      weight='regular'
                      color={colors.NEUTRAL_50}
                    >
                      Following: {audienceData.following_users.toLocaleString()}{' '}
                      User
                      {audienceData.following_users > 1 ? 's' : ''}
                    </Text>
                  ) : null}
                  {audienceData.player_users > 0 ? (
                    <Text
                      variant='paragraphXSmall'
                      weight='regular'
                      color={colors.NEUTRAL_50}
                    >
                      Players:{' '}
                      {(audienceData?.player_users || 0).toLocaleString()} User
                      {audienceData?.player_users > 1 ? 's' : ''}
                    </Text>
                  ) : null}
                  {audienceData.team_official_users > 0 ? (
                    <Text
                      variant='paragraphXSmall'
                      weight='regular'
                      color={colors.NEUTRAL_50}
                    >
                      Team Officials:{' '}
                      {audienceData.team_official_users.toLocaleString()} User
                      {audienceData.team_official_users > 1 ? 's' : ''}
                    </Text>
                  ) : null}
                  <Text
                    variant='paragraphXSmall'
                    weight='semiBold'
                    color={colors.NEUTRAL_50}
                  >
                    Total: {audienceData.total_users.toLocaleString()} Unique
                    Users
                  </Text>
                </>
              ) : null}
            </div>
          )}
        </div>
      )
    }
  )

export default AudienceBreakdownSection
